/* =========================================================================
 * CORRECT THIS FIELD, ON A PURSUIT RECORD — the control, beside the value.
 *
 * Brian: "these all must have read, and write functionality." Corporate first:
 * 1,783 accounts whose OWNER and PURSUIT LEVEL are unset on most of them. That
 * backfill is the reason this exists, and it is why `sr_assigned_rep` and
 * `hs_lead_status` are the two fields everything here is shaped around.
 *
 * ⚠️ IT DRAWS NO FIELD LIST OF ITS OWN. Every field, every label, every type and
 * every enum option comes from `GET /api/pursuit-fields?line=<line>` via
 * `pursuitEdit.js`, which contains no field names at all. `FieldCorrect.jsx`
 * beside it has `FIELD_SPECS` because `champion-fields` serves no `specs` key to
 * replace it with (measured: its top-level keys are `ok · object · tiers ·
 * never · lock · caller`). The pursuit route serves `specs` precisely so this
 * second list does not have to exist — so it does not.
 *
 * ⚠️ THE ENUM SELECT IS THE POINT, NOT A NICETY. `industry` and `hs_lead_status`
 * are both HubSpot enumerations. An enum typed free-hand is accepted by the
 * route and rejected by HubSpot afterwards — the way 68 PSAL records were lost
 * on `sr_segment`. The options are the portal's own, read live.
 * ========================================================================= */

import { useState } from 'react'
import {
  REASON_MIN, specOf, mayEditPursuitField, pursuitEditProblem, renderPursuitValue,
  currentPursuitValue, pursuitEditPatch, canEditPursuitFields, pursuitDerivedNote,
  pursuitEditableFields, pursuitSpecGaps,
} from './pursuitEdit.js'
import { runPursuitEdit, confirmPursuitWrite } from './pursuitEditRun.js'
import { useAccess } from '../../../lib/access.jsx'
import { ACTIONS } from '../../../lib/rbac.js'
import styles from './pursuit.module.css'

export { canEditPursuitFields }

/**
 * The inline control for ONE field. Renders nothing at all when the field is not
 * drawable — a `never` field gets NO control, not a disabled one. On the RFP
 * line that list is `dealstage` and `pipeline`, and the server's own refusal
 * says why: writing them "would make this route the path by which the app
 * advances a human-owned deal — which is a separate decision with its own
 * consequence, not a side effect of a field editor." A greyed-out input invites
 * somebody to ask for exactly that permission.
 */
export default function PursuitFieldCorrect({ record, field, policy, category, onEdited }) {
  const { can, why } = useAccess()
  const [open, setOpen] = useState(false)
  const [next, setNext] = useState(null)
  const [reason, setReason] = useState('')
  const [stage, setStage] = useState('edit') // edit | confirm | working | done
  const [result, setResult] = useState(null)
  const [declared, setDeclared] = useState(false)

  /* ⚠️ A POLICY THAT FAILED TO LOAD REMOVES THE CONTROL. `readPursuitPolicy`
   * gives `ok:false` for anything it cannot parse — including the 400
   * `unknown_line` a tracker line gets — and `mayEditPursuitField` then reports
   * every field as not drawable. No live-looking input that 403s on press. */
  const spec = specOf(policy, field)
  if (!spec || !canEditPursuitFields(record, category) || !policy?.ok) return null

  const gate = mayEditPursuitField(policy, field)
  if (!gate.drawable) return null

  /* ⚠️ MIRRORS THE ROUTE, NOT THE CONSEQUENCE. `CC_CHAMPION_UPDATE` is the
   * correction tier — exec any record, rep own book — and it is what the
   * operational half of `pursuit-update` allows. The PRIVILEGED half is gated
   * separately and by the SERVER, via `canRepairData` in the policy, so a rep
   * sees the eight operational fields live and the five structural ones
   * disabled with the reason naming the grant. Gating wider than the server
   * draws a live button that 403s on every press; gating narrower hides a
   * control somebody is entitled to. DRAWING ONLY (hard rule 5). */
  const mayAct = can(ACTIONS.CC_CHAMPION_UPDATE, record)
  const current = currentPursuitValue(record, field)
  const value = next === null ? current : next

  const problem = pursuitEditProblem({
    policy, field, current, next: value, reason, gate, clearOk: declared,
  })
  const blockedByDeclaration = problem?.declare && !declared
  const ready = mayAct && gate.editable && !problem

  const reset = () => {
    setOpen(false); setNext(null); setReason(''); setStage('edit')
    setResult(null); setDeclared(false)
  }

  const run = async () => {
    setStage('working'); setResult(null)
    const r = await runPursuitEdit({
      record, line: record?.lineKey || category?.line, field, value, reason, gate,
      clearing: !String(value).trim(),
    })
    /* The confirmation needs the policy to tell a date from an enum, and the run
     * half deliberately holds none — so it is composed here. */
    const confirmation = r.confirmation
      ? confirmPursuitWrite({
        policy, field, expected: r.confirmation.expected, afterRaw: r.confirmation.afterRaw,
      })
      : null
    setResult({ verdict: r.verdict, confirmation })
    setStage('done')

    /* ⚠️ ONLY A CONFIRMED WRITE THAT THE RE-READ AGREES WITH REPAINTS THE BOARD.
     * A `mismatch` means the route said yes and HubSpot disagrees; patching on
     * that would put a value on the row the record does not hold. And
     * `pursuitEditPatch` returns null for every field the ADAPTER transforms —
     * `state`, `website`, `sr_assigned_rep`, `city` — so those rows stay as they
     * are until the next real load rather than being repainted from a second
     * copy of the normalizer. */
    if (r.verdict.kind === 'written' && confirmation?.state === 'confirmed') {
      const patch = pursuitEditPatch(field, value)
      if (patch && onEdited) onEdited(String(record.id), patch)
    }
  }

  /* ---- NOT EDITABLE, AND ALWAYS WITH THE REASON ------------------------
   * Shown, never hidden. A rep who cannot see WHY a control is unavailable
   * concludes the feature is broken and asks for it to be fixed — the same
   * reading as a committee member with no email in OutreachPicker.
   *
   * ⚠️ `unavailable` GETS ITS OWN WORD, because the fix is a different one. A
   * privileged refusal is a grant an exec can set; an unavailable field is a
   * HubSpot property that does not exist yet and has to be CREATED. Today that
   * is `sr_pursuit_status` on every company line, and `sr_assigned_rep` on the
   * RFP line — which is the owner property that line declares, so the Owner
   * column there is honestly unwritable rather than mysteriously blank. */
  if (!gate.editable || !mayAct) {
    return (
      <span
        className={gate.unavailable ? styles.fcUnavail : styles.fcLocked}
        title={gate.why || why(ACTIONS.CC_CHAMPION_UPDATE, record) || ''}
      >
        {gate.unavailable ? 'not on portal'
          : gate.tier === 'privileged' ? 'privileged' : 'read-only'}
      </span>
    )
  }

  if (!open) {
    return (
      <button type="button" className={styles.fcOpen} onClick={() => setOpen(true)}>
        Correct
      </button>
    )
  }

  const derived = pursuitDerivedNote(field)

  return (
    <div className={styles.fcPanel}>
      <div className={styles.fcHead}>
        Correct <b>{spec.label}</b>
        {gate.tier === 'privileged' && (
          <span className={styles.fcTier} title={policy.privilegedRequires}>privileged</span>
        )}
      </div>

      {/* ⚠️ BEFORE → AFTER, ALWAYS BOTH, AND THE LABEL NAMES THE RAW PROPERTY.
          This is the STORED value and the row above may legitimately show
          something else: `adaptPursuitRow` runs `state` through `normalizeState`,
          so Charter Communications stores "Connecticut" and the board reads CT —
          live on corporate today. Labelling this "Now" alone would leave the
          operator unable to tell which of two different-looking values they
          were about to replace. */}
      <div className={styles.fcNow}>
        <span className={styles.fcNowK}>Now · {field}</span>
        <span className={styles.fcNowV}>{renderPursuitValue(policy, field, current)}</span>
        {derived && (
          <span className={styles.fcNowNote}>
            This is the stored value. The board derives <b>{derived.shows}</b> from it
            {' '}({derived.via}), so the row above may read differently — and correcting this may not
            change what the board displays.
          </span>
        )}
      </div>

      {/* ⚠️ AN ENUM IS A SELECT OF THE PORTAL'S OWN OPTIONS, NEVER A TEXT BOX. */}
      {spec.kind === 'enum' ? (
        <label className={styles.fcField}>
          <span className={styles.fcFieldK}>
            Change it to <span className={styles.fcOpts}>{spec.options.length} values on the portal</span>
          </span>
          <select
            className={styles.fcSelect}
            value={value}
            onChange={(e) => { setNext(e.target.value); setStage('edit'); setResult(null) }}
          >
            <option value="">(blank)</option>
            {spec.options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      ) : (
        <label className={styles.fcField}>
          <span className={styles.fcFieldK}>Change it to</span>
          <input
            className={styles.fcInput}
            type={spec.kind === 'date' ? 'date' : spec.kind === 'number' ? 'text' : 'text'}
            inputMode={spec.kind === 'number' ? 'numeric' : undefined}
            value={value}
            placeholder={spec.kind === 'date' ? 'YYYY-MM-DD' : ''}
            onChange={(e) => { setNext(e.target.value); setStage('edit'); setResult(null) }}
          />
        </label>
      )}

      {value !== current && (
        <div className={styles.fcDiff}>
          {renderPursuitValue(policy, field, current)} <span className={styles.fcArrow}>→</span>{' '}
          <b>{renderPursuitValue(policy, field, value)}</b>
        </div>
      )}

      {/* ⚠️ THE COPY DOES NOT CLAIM A GUARANTEE THE SERVER DOES NOT MAKE.

          It used to read "Why — this goes to the ledger / required", which
          states two things: that this form wants a reason (true), and that a
          correction to this record carries one into the ledger (NOT true of the
          route). Measured: `POST /api/pursuit-update` accepted an operational
          write with NO reason and answered 200 `wrote:['city']`. A PRIVILEGED
          write genuinely requires one server-side — the `data_repair_locked`
          refusal names it — but an operational one does not.

          A client check is a DRAWING decision (hard rule 5) and this one secures
          nothing: anyone can `curl` the route without a reason. The danger is
          not the missing enforcement, it is somebody later reading the ledger as
          guaranteed to explain every change — and deciding a blank-reason row
          must therefore be an engine or a bug rather than a person with curl.
          So the label says whose requirement it is, and the note below says what
          the route does. The floor itself stays: it is good practice and it is
          what puts a reason in the payload. */}
      <label className={styles.fcField}>
        <span className={styles.fcFieldK}>
          Why — sent with this correction <span className={styles.fcReq}>required here</span>
        </span>
        <textarea
          className={styles.fcReason}
          rows={3}
          value={reason}
          placeholder="What was wrong, and how the new value was established."
          onChange={(e) => { setReason(e.target.value); setStage('edit'); setResult(null) }}
        />
      </label>

      {/* THE HONEST NOTE, on the tier it is true of. A privileged write is
          refused server-side without a reason; an operational one is not, so
          the ledger cannot be read as guaranteed to explain every change to
          these fields. Said here rather than buried in a comment, because the
          person who needs it is the one reading the ledger six months from now
          — and the shape of that mistake is assuming a blank-reason row was a
          machine. */}
      <p className={styles.fcPolicyNote}>
        {gate.tier === 'privileged'
          ? 'This field is privileged: the route refuses it without a reason, so the ledger row for '
            + 'this correction will carry yours.'
          : 'This form requires a reason; the route does not, on an operational field. Yours is sent '
            + 'and recorded — but a ledger row on these fields is not guaranteed to have one.'}
      </p>

      {problem?.declare && (
        <label className={styles.fcDeclare}>
          <input
            type="checkbox"
            checked={declared}
            onChange={(e) => setDeclared(e.target.checked)}
          />
          <span>Yes — clear this field deliberately. {problem.message}</span>
        </label>
      )}

      {problem && !problem.declare && stage === 'edit' && (
        <div className={styles.fcProblem}>{problem.message}</div>
      )}
      {blockedByDeclaration && stage === 'edit' && (
        <div className={styles.fcProblem}>Tick the box above to clear this field.</div>
      )}

      {stage === 'edit' && (
        <div className={styles.fcActions}>
          <button
            type="button"
            className={styles.fcGo}
            disabled={!ready}
            onClick={() => setStage('confirm')}
          >
            Review this correction
          </button>
          <button type="button" className={styles.fcCancel} onClick={reset}>Cancel</button>
        </div>
      )}

      {/* ⚠️ A SECOND, SPECIFIC ACT. Propose-don't-overwrite is the standing rule;
          this is a human deliberately replacing a recorded value on a live
          record, so it takes a deliberate gesture made against the actual before
          and after — not a tap on a button beside a text box. */}
      {stage === 'confirm' && (
        <div className={styles.fcConfirm}>
          <p className={styles.fcAsk}>
            Set <b>{spec.label}</b> to <b>{renderPursuitValue(policy, field, value)}</b>
            {' '}on <b>{record?.name}</b>?
          </p>
          <p className={styles.fcDetail}>
            It currently reads {renderPursuitValue(policy, field, current)}. The old value and your
            reason both go to the ledger, and the record will be read back directly to confirm what
            it actually says afterwards.
          </p>
          <div className={styles.fcActions}>
            <button type="button" className={styles.fcGo} onClick={run}>
              Yes — write it
            </button>
            <button type="button" className={styles.fcCancel} onClick={() => setStage('edit')}>
              Back
            </button>
          </div>
        </div>
      )}

      {stage === 'working' && (
        <div className={styles.fcWorking}>Writing, then reading the record back…</div>
      )}

      {stage === 'done' && result && (
        <PursuitVerdict result={result} onClose={reset} />
      )}
    </div>
  )
}

/* ---- WHAT THE SERVER ACTUALLY SAID -------------------------------------
 *
 * ⚠️ ONLY `written` + `confirmed` IS A SUCCESS, AND IT TAKES BOTH.
 *
 * `written` means the route named the field in `wrote` — which it will do for an
 * enum value HubSpot then rejects, and which it has done while changing nothing.
 * So the re-read qualifies it, and the three qualified outcomes are drawn as
 * three different things rather than collapsed:
 *
 *   written + confirmed   → done, and the record says so
 *   written + mismatch    → ⚠️ NOT corrected, in the danger tone, and it is the
 *                           loudest state here — every other zero-write outcome
 *                           left the record alone and this one did not
 *   written + unverified  → the write landed, what it reads now is unknown
 */
function PursuitVerdict({ result, onClose }) {
  const { verdict, confirmation } = result

  if (verdict.kind === 'written') {
    if (confirmation?.state === 'confirmed') {
      return (
        <div className={styles.fcOk}>
          <b>Corrected.</b> {confirmation.message} {verdict.message}
          <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
        </div>
      )
    }
    if (confirmation?.state === 'mismatch') {
      return (
        <div className={styles.fcErr}>
          <b>The write was accepted and the record does not agree.</b>
          <div>{confirmation.message}</div>
          <div className={styles.fcHint}>{confirmation.hint}</div>
          <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
        </div>
      )
    }
    return (
      <div className={styles.fcWarn}>
        <b>Written, not verified.</b>
        <div>{confirmation?.message || verdict.message}</div>
        {confirmation?.hint && <div className={styles.fcHint}>{confirmation.hint}</div>}
        <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
      </div>
    )
  }

  /* `denied` is the grant gate and it gets its own wording, because the fix is a
   * named, specific thing — an exec sets `cc_data_repair` — and "refused" on its
   * own would send somebody to ask for the wrong thing. */
  return (
    <div className={verdict.kind === 'denied' ? styles.fcDenied : styles.fcRefused}>
      <b>
        {verdict.kind === 'denied' ? 'Not permitted — nothing was written.'
          : verdict.kind === 'nothing-written' ? 'Nothing was written.'
            : verdict.kind === 'error' ? 'Did not run.'
              : 'Refused.'}
      </b>
      <div>{verdict.message}</div>
      {verdict.hint && <div className={styles.fcHint}>{verdict.hint}</div>}
      <button type="button" className={styles.fcCancel} onClick={onClose}>Close</button>
    </div>
  )
}

/* ---- THE ROW WRAPPER ---------------------------------------------------
 * A record-grid row that carries its own correction control. The editor expands
 * BENEATH the row rather than replacing it, so the value being corrected stays
 * visible while it is being corrected.
 */
export function PursuitEditableRow({ k, field, record, policy, category, onEdited, children }) {
  return (
    <div className={styles.fcRow}>
      <div className={styles.fcRowTop}>
        <div className={styles.fcRowK}>{k}</div>
        <div className={styles.fcRowV}>
          {children}
          <PursuitFieldCorrect
            /* Keyed by record AND field — the drawer is reused when the operator
               opens a different account, and without this an open form would
               carry the previous record's typed value and reason onto a
               different record. */
            key={`${record?.id}:${field}`}
            record={record}
            field={field}
            policy={policy}
            category={category}
            onEdited={onEdited}
          />
        </div>
      </div>
    </div>
  )
}

/* ---- THE WHOLE CORRECTABLE SET, DRAWN FROM THE POLICY ------------------
 *
 * ⚠️ THE ROWS ARE NOT HAND-WRITTEN, AND THAT IS THE DIFFERENCE FROM THE
 * CHAMPIONSHIP DRAWER. There, nine `<ER k="Sport" field="sr_sport">` rows are
 * typed out by hand against `FIELD_SPECS` — which is the hardcoded list this
 * build exists to avoid reproducing. A hand-written row list would be that list
 * again wearing JSX: a field added to a tier server-side would not appear, and
 * one renamed would render as an empty row nobody could explain.
 *
 * So the grid IS `pursuitEditableFields(policy, record)` — the server's fields,
 * in the server's order (operational first: who owns it and how warm it is;
 * privileged after: the identity that would move it out of the space), each
 * labelled with the portal's own label. A tier change reaches this UI with no
 * frontend edit at all.
 */
export function PursuitFieldGrid({ record, policy, category, onEdited }) {
  if (!policy?.ok || !canEditPursuitFields(record, category)) return null

  const fields = pursuitEditableFields(policy, record)
  if (!fields.length) return null

  const gaps = pursuitSpecGaps(policy)

  return (
    <>
      {fields.map((f) => (
        <PursuitEditableRow
          key={f.field}
          k={f.spec.label}
          field={f.field}
          record={record}
          policy={policy}
          category={category}
          onEdited={onEdited}
        >
          <span className={styles.fcRowVal}>
            {renderPursuitValue(policy, f.field, f.current) }
          </span>
          {/* THE OWNER FIELD, NAMED. It is the property the policy declares
              (`owner.property`), read rather than assumed — it is one env var
              from changing — and it is the field the whole corporate backfill
              is about. */}
          {f.isOwner && <span className={styles.fcOwnerTag}>owner</span>}
        </PursuitEditableRow>
      ))}

      {/* ⚠️ DECLARED BY THE POLICY, ABSENT FROM THE PORTAL — NAMED, NOT OMITTED.
          `mayEditPursuitField` reports these as drawable-but-not-editable, and
          the reason that check runs BEFORE the tier check is that an
          unavailable property is usually in no tier either (the server has
          nothing to tier a property that does not exist), so it would otherwise
          fall into "renamed server-side" and vanish.
          It has no spec, so it cannot be a labelled row — but it can be said
          out loud, which is the whole point: a rep looking for pursuit status
          finds the reason there is no control rather than concluding the editor
          is broken. Today this is `sr_pursuit_status` on every company line. */}
      {Object.keys(policy.unavailable || {}).length > 0 && (
        <div className={styles.fcPolicyNote}>
          <b>Not correctable here:</b>{' '}
          {Object.keys(policy.unavailable).join(', ')} — declared by this line&apos;s policy but the
          property does not exist on the portal, so a write would be accepted by the route and
          silently dropped by HubSpot. The property has to be created first.
        </div>
      )}

      {/* ⚠️ A FIELD THE POLICY DECLARES AND SERVES NO SPEC FOR IS REPORTED, NOT
          SWALLOWED. Without a label and a type it would be an unlabelled
          free-text box over a live write route, so it is not drawn — and this
          is how anybody finds out, rather than discovering that a correction
          quietly stopped being offered. Empty on all eight lines today. */}
      {gaps.length > 0 && (
        <div className={styles.fcDrift}>
          The field policy lists {gaps.join(', ')} but describes {gaps.length === 1 ? 'it' : 'them'}
          {' '}with no label or type, so {gaps.length === 1 ? 'it is' : 'they are'} not drawn. That is
          a gap in <code>/api/pursuit-fields</code>, not a permission.
        </div>
      )}

      <div className={styles.fcPolicyNote}>
        These are the fields <code>/api/pursuit-fields?line={policy.line}</code> reports as writable
        on {policy.objectType}, in the server&apos;s own order. Values shown are the STORED HubSpot
        properties. Every correction needs a reason, goes to the ledger, and is read back to confirm
        what the record actually says afterwards.
      </div>
    </>
  )
}

export { REASON_MIN, pursuitEditableFields, pursuitSpecGaps }
